import React, { useState, useEffect } from "react";
import { X, Save, Loader2, AlertCircle } from "lucide-react";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  useDroppable,
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable";
import { useAppDispatch, useAppSelector } from "../../hooks/useStore";
import {
  createShipmentGroup,
  updateShipmentGroup,
  assignGroupToDriverApp,
  removeGroupFromDriverApp,
} from "../../store/slices/shipmentGroupSlice";
import { withVersion } from "../../utils/concurrency";
import type {
  ShipmentGroup,
  Shipment,
  CreateShipmentGroupData,
  UpdateShipmentGroupData,
  ShipmentGroupShipment,
} from "../../types";
import SortableShipmentCard from "./SortableShipmentCard";
import Button from "../UI/Button";
import { vehicleLabel } from "../../utils/vehicleLabel";

/* -------------------- DROPPABLE CONTAINER -------------------- */
const DroppableContainer: React.FC<{
  id: string;
  className?: string;
  children: React.ReactNode;
}> = ({ id, className, children }) => {
  const { setNodeRef, isOver } = useDroppable({ id });

  return (
    <div
      ref={setNodeRef}
      className={`${className} ${isOver ? "ring-2 ring-blue-400" : ""}`}
    >
      {children}
    </div>
  );
};

interface ShipmentGroupModalProps {
  isOpen: boolean;
  onClose: (isCancel: boolean) => void;
  group?: ShipmentGroup | null;
  mode: "create" | "edit" | "view";
}

const ShipmentGroupModal: React.FC<ShipmentGroupModalProps> = ({
  isOpen,
  onClose,
  group,
  mode,
}) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation("shipmentGroup");
  const { shipments } = useAppSelector((state) => state.shipment);
  const { users } = useAppSelector((state) => state.users);
  const { vehicles } = useAppSelector((state) => state.vehicles);

  const [selectedDriver, setSelectedDriver] = useState<string>("");
  const [selectedTruck, setSelectedTruck] = useState<string>("");
  const [selectedTrailer, setSelectedTrailer] = useState<string>("");

  const [groupedShipments, setGroupedShipments] = useState<Shipment[]>([]);
  const [availableShipments, setAvailableShipments] = useState<Shipment[]>([]);

  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isGroupLocked =
    mode === "edit" &&
    groupedShipments.length > 0 &&
    groupedShipments.every((s) => s.status === "Completed");

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
  );

  const drivers = users.filter((u) => u.role === "driver");
  const trucks = vehicles.filter((v) => v.type === "TRUCK");
  const trailers = vehicles.filter((v) => v.type === "TRAILER");

  useEffect(() => {
    if (isOpen) {
      loadInitialData();
    }
  }, [isOpen, group, shipments]);

  const loadInitialData = () => {
    try {
      setError("");
      if (group && mode !== "create") {
        setSelectedDriver(group.assignedDriver?._id || "");
        setSelectedTruck(
          typeof group.truckLicensePlate === "object"
            ? group.truckLicensePlate?._id || ""
            : "",
        );
        setSelectedTrailer(
          typeof group.trailerLicensePlate === "object"
            ? group.trailerLicensePlate?._id || ""
            : "",
        );

        const grouped = [...group.shipments]
          .sort((a, b) => a.order - b.order)
          .map((gs) => gs.shipment);
        setGroupedShipments(grouped as Shipment[]);

        const available = shipments.filter(
          (s) =>
            !s.groupId &&
            (s.status === "Order" || s.status === "ReadyForPickup"),
        );
        setAvailableShipments(available);
      } else {
        setGroupedShipments([]);
        const available = shipments.filter(
          (s) =>
            !s.groupId &&
            (s.status === "Order" || s.status === "ReadyForPickup"),
        );
        setAvailableShipments(available);
      }
    } catch (err) {
      console.error("Failed to load data:", err);
      setError(t("modal.error.loadFailed"));
    }
  };

  useEffect(() => {
    if (mode === "create" || mode === "edit") {
      const available = shipments.filter(
        (s) => !s.groupId && !groupedShipments.find((gs) => gs._id === s._id),
      );
      setAvailableShipments(available);
    }
  }, [shipments, mode]);

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || isReadOnly) return;

    const activeId = active.id as string;
    const overId = over.id as string;

    if (activeId === overId) return;

    const activeInGrouped = groupedShipments.some((s) => s._id === activeId);
    const activeInAvailable = availableShipments.some(
      (s) => s._id === activeId,
    );

    if (
      activeInAvailable &&
      (overId === "grouped" || groupedShipments.some((s) => s._id === overId))
    ) {
      const shipment = availableShipments.find((s) => s._id === activeId);
      if (!shipment) return;
      setAvailableShipments((prev) => prev.filter((s) => s._id !== activeId));
      setGroupedShipments((prev) => [...prev, shipment]);
      return;
    }

    if (
      activeInGrouped &&
      (overId === "available" ||
        availableShipments.some((s) => s._id === overId))
    ) {
      const shipment = groupedShipments.find((s) => s._id === activeId);
      if (!shipment) return;
      setGroupedShipments((prev) => prev.filter((s) => s._id !== activeId));
      setAvailableShipments((prev) => [...prev, shipment]);
      return;
    }

    if (activeInGrouped && groupedShipments.some((s) => s._id === overId)) {
      setGroupedShipments((prev) =>
        arrayMove(
          prev,
          prev.findIndex((s) => s._id === activeId),
          prev.findIndex((s) => s._id === overId),
        ),
      );
      return;
    }

    if (activeInAvailable && availableShipments.some((s) => s._id === overId)) {
      setAvailableShipments((prev) =>
        arrayMove(
          prev,
          prev.findIndex((s) => s._id === activeId),
          prev.findIndex((s) => s._id === overId),
        ),
      );
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    setError(null);

    try {
      const shipmentsWithOrder: ShipmentGroupShipment[] = groupedShipments.map(
        (shipment, index) => ({
          shipment: shipment._id,
          order: index + 1,
        }),
      );

      if (mode === "create") {
        const groupData: CreateShipmentGroupData = {
          assignedDriver: selectedDriver || null,
          truckLicensePlate: selectedTruck || null,
          trailerLicensePlate: selectedTrailer || null,
          shipments: shipmentsWithOrder,
        };

        await dispatch(createShipmentGroup(groupData)).unwrap();
        toast.success(t("modal.toast.createSuccess"));
      } else if (mode === "edit" && group) {
        const groupData: UpdateShipmentGroupData = {
          assignedDriver: selectedDriver || null,
          truckLicensePlate: selectedTruck || null,
          trailerLicensePlate: selectedTrailer || null,
          shipments: shipmentsWithOrder,
        };

        await dispatch(
          updateShipmentGroup({
            id: group._id,
            groupData: withVersion(groupData, group),
          }),
        ).unwrap();
        toast.success(t("modal.toast.updateSuccess"));
      }

      onClose(false);
    } catch (err) {
      const errorMessage =
        typeof err === "string"
          ? err
          : err instanceof Error
            ? err.message
            : t("modal.error.saveFailed");
      setError(errorMessage);
    } finally {
      setIsSaving(false);
    }
  };

  const isAssignedToDriverApp =
    groupedShipments.length > 0 &&
    groupedShipments.every((s) => s.assignedToDriverApp === true);

  const hasDriver = !!selectedDriver;

  const [isDriverAppLoading, setIsDriverAppLoading] = useState(false);

  const handleDriverAppToggle = async () => {
    if (!group?._id) return;
    setIsDriverAppLoading(true);
    try {
      if (isAssignedToDriverApp) {
        await dispatch(removeGroupFromDriverApp(group._id)).unwrap();
        setGroupedShipments((prev) =>
          prev.map((s) => ({ ...s, assignedToDriverApp: false })),
        );
        toast.success(t("modal.toast.unassignSuccess"));
      } else {
        await dispatch(assignGroupToDriverApp(group._id)).unwrap();
        setGroupedShipments((prev) =>
          prev.map((s) => ({ ...s, assignedToDriverApp: true })),
        );
        toast.success(t("modal.toast.assignSuccess"));
      }
    } catch (err) {
      toast.error(err as string);
    } finally {
      setIsDriverAppLoading(false);
    }
  };

  const isReadOnly = mode === "view" || isGroupLocked;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-300">
          <h2 className="text-xl font-semibold">
            {mode === "create" ? t("modal.createTitle") : group?.groupNumber}
          </h2>
          <button
            onClick={() => onClose(true)}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {isGroupLocked && (
          <div className="px-6 py-3 bg-yellow-50 border-b border-yellow-200 text-sm text-yellow-800">
            {t("modal.lockedBanner")}
          </div>
        )}

        {error && (
          <div className="px-6 py-3 bg-red-50 border-b border-red-200 flex items-center space-x-2">
            <AlertCircle className="h-5 w-5 text-red-600" />
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        {/* Assignment Fields */}
        <div className="px-6 py-4 border-b border-gray-300">
          <div className="grid grid-cols-3 gap-4">
            {/* Driver */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t("modal.labels.driver")}
              </label>
              <select
                value={selectedDriver}
                onChange={(e) => setSelectedDriver(e.target.value)}
                disabled={isReadOnly}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
              >
                <option value="">{t("modal.placeholders.driver")}</option>
                {drivers.map((driver) => (
                  <option key={driver._id} value={driver._id}>
                    {driver.firstName} {driver.lastName}
                  </option>
                ))}
              </select>
            </div>

            {/* Truck */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t("modal.labels.truck")}
              </label>
              <select
                value={selectedTruck}
                onChange={(e) => setSelectedTruck(e.target.value)}
                disabled={isReadOnly}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
              >
                <option value="">{t("modal.placeholders.truck")}</option>
                {trucks.map((truck) => (
                  <option key={truck._id} value={truck._id}>
                    {vehicleLabel(truck)}
                  </option>
                ))}
              </select>
            </div>

            {/* Trailer */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t("modal.labels.trailer")}
              </label>
              <select
                value={selectedTrailer}
                onChange={(e) => setSelectedTrailer(e.target.value)}
                disabled={isReadOnly}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
              >
                <option value="">{t("modal.placeholders.trailer")}</option>
                {trailers.map((trailer) => (
                  <option key={trailer._id} value={trailer._id}>
                    {vehicleLabel(trailer)}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 flex overflow-hidden">
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <div className="flex-1 flex overflow-hidden">
              {/* AVAILABLE */}
              <div className="w-1/2 flex flex-col border-r border-gray-300 overflow-hidden">
                <div className="p-4 font-medium">
                  {t("modal.sections.available", {
                    count: availableShipments.length,
                  })}
                </div>
                <div className="flex-1 overflow-y-auto p-4">
                  <DroppableContainer
                    id="available"
                    className="min-h-full p-2 border-2 border-gray-300 border-dashed rounded-lg"
                  >
                    <SortableContext
                      items={availableShipments.map((s) => s._id)}
                      strategy={verticalListSortingStrategy}
                    >
                      <div className="space-y-2">
                        {availableShipments.map((s) => (
                          <SortableShipmentCard
                            key={s._id}
                            shipment={s}
                            isReadOnly={isReadOnly}
                          />
                        ))}
                      </div>
                    </SortableContext>
                  </DroppableContainer>
                </div>
              </div>

              {/* GROUPED */}
              <div className="w-1/2 flex flex-col overflow-hidden">
                <div className="p-4 font-medium">
                  {t("modal.sections.grouped", {
                    count: groupedShipments.length,
                  })}
                </div>
                <div className="flex-1 overflow-y-auto p-4">
                  <DroppableContainer
                    id="grouped"
                    className="min-h-full p-2 border-2 border-gray-300 border-dashed rounded-lg bg-blue-50"
                  >
                    <SortableContext
                      items={groupedShipments.map((s) => s._id)}
                      strategy={verticalListSortingStrategy}
                    >
                      <div className="space-y-2">
                        {groupedShipments.map((s, i) => (
                          <SortableShipmentCard
                            key={s._id}
                            shipment={s}
                            order={i + 1}
                            isReadOnly={isReadOnly}
                          />
                        ))}
                      </div>
                    </SortableContext>
                  </DroppableContainer>
                </div>
              </div>
            </div>
          </DndContext>
        </div>

        {/* Footer */}
        <div className="flex justify-between items-center gap-3 p-6 border-t border-gray-300">
          <div>
            {mode === "edit" &&
              !isGroupLocked &&
              hasDriver &&
              groupedShipments.length > 0 && (
                <Button
                  onClick={handleDriverAppToggle}
                  variant={isAssignedToDriverApp ? "outline" : "primary"}
                  type="button"
                  disabled={isDriverAppLoading}
                >
                  {isDriverAppLoading ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      {isAssignedToDriverApp
                        ? t("modal.buttons.unassigning")
                        : t("modal.buttons.assigning")}
                    </>
                  ) : isAssignedToDriverApp ? (
                    t("modal.buttons.unassignDriverApp")
                  ) : (
                    t("modal.buttons.assignDriverApp")
                  )}
                </Button>
              )}
          </div>

          <div className="flex gap-3">
            <button
              onClick={() => onClose(true)}
              className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            >
              {isReadOnly
                ? t("modal.buttons.close")
                : t("modal.buttons.cancel")}
            </button>
            {!isReadOnly && (
              <Button onClick={handleSave} disabled={isSaving}>
                {isSaving ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    {t("modal.buttons.saving")}
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4" />
                    {t("modal.buttons.save")}
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShipmentGroupModal;
